/*
  Madam Muse cart drawer behaviour (snippets/mm-cart-drawer.liquid, 2026-10-15).
  Horizon keeps the drawer (open/close, focus, auto-open, morph). This module:
  - quantity steppers, remove (every line on its own, Kaching bundle lines too), upsell add-to-cart (photo follows colour);
  - after each change dispatches Horizon's CartLinesUpdateEvent, so <cart-items-component> re-renders the drawer and the
    header bubble updates;
  - bridges adds that bypass Horizon (Kaching Bundles posts straight to /cart/add.js): the drawer refreshes and opens.
*/
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const nativeFetch = window.fetch.bind(window);
let ownRequest = 0; // > 0 while this module is talking to the cart API
let announcing = false; // true while this module dispatches its own cart event

const drawer = () => document.getElementById('cart-drawer');

/** Tell Horizon the cart changed; it re-renders the drawer section and the header count. */
async function announce(action, lines = []) {
  const deferred = CartLinesUpdateEvent.createPromise();
  // dispatched from outside <cart-items-component>: Horizon only re-renders the drawer itself for "external" updates
  // (an event from inside it, e.g. the empty bag after a Kaching add, left the empty state on screen)
  const source = document.body;
  announcing = true;
  source.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
  announcing = false;
  try {
    ownRequest++;
    const cart = await (await nativeFetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();
    // fetch the re-rendered drawer too and morph it here: Horizon's own refresh did not run for an add into the empty
    // bag (the empty state stayed on screen with 2 bras in the cart)
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    let sections = null;
    if (sectionIds.length) {
      try {
        sections = await (await nativeFetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`)).json();
      } catch (e) { sections = null; }
    }
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections, source: 'mm-cart' } });
    for (const id of sectionIds) {
      if (sections && sections[id]) {
        try { await morphSection(id, sections[id], { mode: 'hydration' }); } catch (e) { console.warn('[mm-cart] morph', e); }
      }
    }
    return cart;
  } catch (e) {
    deferred.reject(e);
    return null;
  } finally {
    ownRequest--;
  }
}

async function post(path, body) {
  ownRequest++;
  try {
    const res = await nativeFetch(`${ROOT}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || 'Cart update failed');
    return json;
  } finally {
    ownRequest--;
  }
}

/**
 * One round trip: update the cart and get the re-rendered drawer HTML in the same response (Section Rendering API),
 * then hand both to Horizon through the event so it morphs without fetching cart.js and the section again.
 */
async function updateCart(updates) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  const source = document.querySelector('mm-cart') || document.body;
  const lines = Object.entries(updates).map(([id, quantity]) => ({ id, quantity }));
  announcing = true;
  source.dispatchEvent(new CartLinesUpdateEvent({ action: 'change', context: 'cart', lines, promise: deferred.promise }));
  announcing = false;
  try {
    const json = await post('cart/update.js', { updates, sections: sectionIds.join(','), sections_url: window.location.pathname });
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
      detail: { items: json.items, sections: json.sections, source: 'mm-cart' },
    });
    // Horizon's cart-items listener does not reliably apply these sections to our drawer markup: morph them here
    // (same call Horizon uses; a second identical morph is a no-op)
    for (const id of sectionIds) {
      if (json.sections && json.sections[id]) {
        try { await morphSection(id, json.sections[id], { mode: 'hydration' }); } catch (e) { console.warn('[mm-cart] morph', e); }
      }
    }
    return json;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}

function busy(on) {
  const el = document.querySelector('mm-cart');
  if (el) el.classList.toggle('is-busy', on);
}

/* ---------- clicks inside the drawer ---------- */
document.addEventListener('click', async (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t || !t.closest('mm-cart, .mmc-foot')) return;

  const qtyBtn = t.closest('[data-mmc-qty]');
  const removeBtn = t.closest('[data-mmc-remove]');
  const addBtn = t.closest('[data-mmc-upsell-add]');

  if (qtyBtn || removeBtn) {
    e.preventDefault();
    const updates = {};
    if (qtyBtn) updates[qtyBtn.dataset.mmcQty] = Math.max(0, parseInt(qtyBtn.dataset.qty, 10) || 0);
    else removeBtn.dataset.mmcRemove.split(',').filter(Boolean).forEach((k) => { updates[k] = 0; });
    // react on the click: a removed pack/line fades out at once while the cart updates
    const row = removeBtn && removeBtn.closest('[data-mmc-line]');
    if (row) row.classList.add('is-removing');
    // and a quantity change shows the new number at once
    const qtyText = qtyBtn && qtyBtn.parentElement.querySelector('span');
    if (qtyText) qtyText.textContent = String(updates[qtyBtn.dataset.mmcQty]);
    busy(true);
    try {
      await updateCart(updates);
    } catch (err) {
      console.warn('[mm-cart]', err);
      if (row) row.classList.remove('is-removing');
      await announce('change');
    } finally {
      busy(false);
    }
  }

  if (addBtn) {
    e.preventDefault();
    const card = addBtn.closest('.mmc-upsell');
    const field = card.querySelector('[data-mmc-upsell-variant]');
    const err = card.querySelector('[data-mmc-upsell-error]');
    const id = field && field.value;
    if (!id) {
      err.textContent = 'Please choose your size first.';
      err.hidden = false;
      const empty = [...card.querySelectorAll('[data-mmc-upsell-option]')].find((s) => !s.value);
      if (empty) empty.focus();
      return;
    }
    err.hidden = true;
    addBtn.disabled = true;
    busy(true);
    try {
      await post('cart/add.js', { items: [{ id: Number(id), quantity: 1 }] });
      // upsell deal: add the hidden code next to any codes the shopper already has
      const code = card.dataset.mmcDealCode;
      if (code) {
        try {
          ownRequest++;
          const now = await (await nativeFetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();
          const codes = (now.discount_codes || []).map((c) => c.code).filter((c) => c && c.toUpperCase() !== code.toUpperCase());
          await post('cart/update.js', { discount: [...codes, code].join(',') });
        } catch (dErr) {
          console.warn('[mm-cart] deal code', dErr);
        } finally {
          ownRequest--;
        }
      }
      await announce('add', [{ merchandiseId: String(id), quantity: 1 }]);
    } catch (ex) {
      err.textContent = ex.message || 'Could not add this item.';
      err.hidden = false;
    } finally {
      addBtn.disabled = false;
      busy(false);
    }
  }
});

/* ---------- upsell option selects -> variant id ---------- */
document.addEventListener('change', (e) => {
  const sel = e.target instanceof Element ? e.target.closest('[data-mmc-upsell-option]') : null;
  if (!sel) return;
  const card = sel.closest('.mmc-upsell');
  let variants = [];
  try { variants = JSON.parse(card.querySelector('[data-mmc-upsell-variants]').textContent); } catch (err) { return; }
  const chosen = [...card.querySelectorAll('[data-mmc-upsell-option]')].map((s) => s.value);
  const match = chosen.every(Boolean) ? variants.find((v) => v.o.every((val, i) => val === chosen[i])) : null;
  // the photo follows the chosen colour (any variant matching the options picked so far that has its own image)
  const pic = (match && match.img && match) || variants.find((v) => v.img && chosen.every((val, i) => !val || v.o[i] === val));
  const img = card.querySelector('.mmc-upsell__img img');
  if (pic && img && img.getAttribute('src') !== pic.img) {
    img.removeAttribute('srcset');
    img.src = pic.img;
  }
  card.querySelector('[data-mmc-upsell-variant]').value = match && match.a ? match.id : '';
  const err = card.querySelector('[data-mmc-upsell-error]');
  if (match && !match.a) { err.textContent = 'That combination is sold out.'; err.hidden = false; } else { err.hidden = true; }
});

/* ---------- upsell deal: drop the hidden code once no bra is left (the drawer sets data-mmc-deal-clear) ---------- */
let clearing = false;
async function clearDeal() {
  const el = document.querySelector('mm-cart[data-mmc-deal-clear]');
  if (!el || clearing) return;
  const code = el.dataset.mmcDealClear.toUpperCase();
  clearing = true;
  try {
    let now;
    ownRequest++;
    try {
      now = await (await nativeFetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();
    } finally {
      ownRequest--;
    }
    const codes = (now.discount_codes || []).map((c) => c.code).filter(Boolean);
    if (codes.some((c) => c.toUpperCase() === code)) {
      await post('cart/update.js', { discount: codes.filter((c) => c.toUpperCase() !== code).join(',') });
      await announce('change');
    }
  } catch (e) {
    console.warn('[mm-cart] deal clear', e);
  } finally {
    clearing = false;
  }
}
new MutationObserver(() => { if (document.querySelector('mm-cart[data-mmc-deal-clear]')) clearDeal(); })
  .observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-mmc-deal-clear'] });
// first check after load, so Horizon's cart components are listening for the re-render event
const firstClear = () => setTimeout(clearDeal, 300);
if (document.readyState === 'complete') firstClear(); else window.addEventListener('load', firstClear, { once: true });

/* ---------- Final Offer gift: keep the free underwear line in step with the bras (the drawer sets the flags) ---------- */
// data-mmc-gift-add = gift variant to add (enough bras, no gift line); data-mmc-gift-set = "key:qty," fixes (back to 1, or
// 0 once the bras drop below the minimum). A re-render only, never an auto-open: this also runs on page load.
let gifting = false;
let giftLast = { sig: '', at: 0 };
async function syncGift() {
  const el = document.querySelector('mm-cart[data-mmc-gift-add], mm-cart[data-mmc-gift-set]');
  if (!el || gifting) return;
  const add = el.dataset.mmcGiftAdd || '';
  const set = el.dataset.mmcGiftSet || '';
  const sig = `${add}|${set}`;
  // the same fix again within a few seconds means the last one did not change the cart: stop instead of looping
  if (sig === giftLast.sig && Date.now() - giftLast.at < 5000) return;
  giftLast = { sig, at: Date.now() };
  gifting = true;
  try {
    if (set) {
      const updates = {};
      set.split(',').filter(Boolean).forEach((pair) => {
        const i = pair.lastIndexOf(':'); // line keys contain ':' themselves
        updates[pair.slice(0, i)] = Number(pair.slice(i + 1)) || 0;
      });
      await updateCart(updates);
    } else if (add) {
      await post('cart/add.js', { items: [{ id: Number(add), quantity: 1, properties: { _mm_gift: 'underwear' } }] });
      await announce('change', [{ merchandiseId: String(add), quantity: 1 }]);
    }
  } catch (e) {
    console.warn('[mm-cart] gift', e);
  } finally {
    gifting = false;
  }
}
new MutationObserver(() => { if (document.querySelector('mm-cart[data-mmc-gift-add], mm-cart[data-mmc-gift-set]')) syncGift(); })
  .observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-mmc-gift-add', 'data-mmc-gift-set'] });
const firstGift = () => setTimeout(syncGift, 400);
if (document.readyState === 'complete') firstGift(); else window.addEventListener('load', firstGift, { once: true });

/* ---------- bridge for adds that bypass Horizon (Kaching Bundles) ---------- */
let lastHorizonEvent = 0;
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (announcing) return;
  lastHorizonEvent = Date.now();
  // Horizon's cart-items listener does not reliably morph our drawer after its product form adds (2026-10-20: a second
  // bra added from a regular PDP left the drawer on the 1-line version, so the gift guard never saw 2 bras). Once its
  // event settles, morph from the sections it got (or fetch them); a second identical morph is a no-op.
  if (event.action !== 'add' || !event.promise) return;
  event.promise.then((res) => {
    const detail = res && res.detail;
    if (!detail || detail.didError || detail.source === 'mm-cart' || detail.source === 'mm-gift-offer') return;
    setTimeout(() => refreshDrawer(detail.sections), 0);
  }).catch(() => {});
}, true);

async function refreshDrawer(sections) {
  const ids = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
  if (!ids.length) return;
  let s = sections;
  if (!s || !ids.every((id) => s[id])) {
    ownRequest++;
    try {
      s = await (await nativeFetch(`${window.location.pathname}?sections=${ids.join(',')}`)).json();
    } catch (e) {
      s = null;
    } finally {
      ownRequest--;
    }
  }
  for (const id of ids) {
    if (s && s[id]) {
      try { await morphSection(id, s[id], { mode: 'hydration' }); } catch (e) { console.warn('[mm-cart] morph', e); }
    }
  }
}

function isCartAdd(url, init) {
  const u = String((url && url.url) || url || '');
  const method = ((init && init.method) || (url && url.method) || 'GET').toUpperCase();
  return method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(u.replace(/^https?:\/\/[^/]+/, ''));
}

function fromHorizon(init) {
  // Horizon's product form sends FormData carrying "sections", and dispatches its own event just before fetching
  // Kaching copies that FormData (sections included) and adds its bundle property, then posts it itself: not Horizon
  const body = init && init.body;
  if (body instanceof FormData && body.has('properties[__kaching_bundles]')) return false;
  if (body instanceof FormData && body.has('sections')) return true;
  return Date.now() - lastHorizonEvent < 400;
}

window.fetch = function mmFetch(url, init) {
  const watch = !ownRequest && isCartAdd(url, init) && !fromHorizon(init);
  const p = nativeFetch(url, init);
  if (watch) {
    p.then((res) => {
      if (!res.ok) return;
      setTimeout(async () => {
        await announce('add');
        const d = drawer();
        if (d && !d.hasAttribute('open') && typeof d.open === 'function') d.open();
      }, 50);
    }).catch(() => {});
  }
  return p;
};
